import { Check } from "lucide-react";
import { FEATURES, LIMITS, PLANS, type Plan } from "@sprout/shared/plans";
import { DASHBOARD_URL } from "./links";

function price(plan: Plan) {
  const monthly = plan.prices.find((p) => p.interval === "month");
  const annual = plan.prices.find((p) => p.interval === "year");
  const format = (amount: number, currency: string) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: amount % 100 ? 2 : 0 }).format(amount / 100);
  if (!monthly && !annual) return { main: "Free", note: "Always free for families" };
  return {
    main: monthly ? `${format(monthly.amount, monthly.currency)}/mo` : `${format(annual!.amount, annual!.currency)}/yr`,
    note: annual && monthly ? `or ${format(annual.amount, annual.currency)} a year` : "",
  };
}

/** Plans and features come from the platform catalog (packages/shared/data). */
export default function Pricing() {
  const highlighted = ["wallDevices", "proofStorageMb", "proofRetentionMaxDays", "maxVideoSeconds"] as const;
  return (
    <section className="site-pricing" aria-labelledby="pricing-title">
      <h2 id="pricing-title">Simple plans</h2>
      <p>The core family tools stay free. Plus adds room to grow.</p>
      <div className="site-plans">
        {PLANS.map((plan) => {
          const shown = price(plan);
          const extras = plan.features.filter((f) => !PLANS[0]!.features.includes(f) || plan.id === PLANS[0]!.id);
          return (
            <article key={plan.id} className={"site-plan" + (plan.prices.length ? " paid" : "")}>
              <header>
                <h3>{plan.name}</h3>
                <p>{plan.tagline}</p>
                <strong>{shown.main}</strong>
                {shown.note && <small>{shown.note}</small>}
              </header>
              <ul>
                {plan.id !== PLANS[0]!.id && (
                  <li>
                    <Check size={16} /> Everything in {PLANS[0]!.name}
                  </li>
                )}
                {extras.map((id) => {
                  const feature = FEATURES.find((f) => f.id === id);
                  return feature ? (
                    <li key={id} title={feature.description}>
                      <Check size={16} /> {feature.name}
                    </li>
                  ) : null;
                })}
                {highlighted.map((id) => {
                  const limit = LIMITS.find((l) => l.id === id)!;
                  const value = plan.limits[id];
                  return (
                    <li key={id} className="site-plan-limit">
                      <Check size={16} /> {limit.name}: {id === "proofStorageMb" && value >= 1024 ? `${value / 1024} GB` : `${value} ${limit.unit}`}
                    </li>
                  );
                })}
              </ul>
              <a className={"site-btn " + (plan.prices.length ? "primary" : "secondary")} href={DASHBOARD_URL + "#sign-up"}>
                {plan.prices.length ? "Start free, upgrade anytime" : "Create a family"}
              </a>
            </article>
          );
        })}
      </div>
    </section>
  );
}
